import { create } from "zustand";
import type {
  Experiment,
  ExperimentAssignment,
  ExperimentOutcome,
  AssignmentStatus,
} from "@andrum/schemas";
import {
  assignExperiment as assignExperimentApi,
  completeAssignment as completeAssignmentApi,
  ensureTodayAssignment,
  listOutcomes,
  listPublishedExperiments,
  skipAssignment as skipAssignmentApi,
  startAssignment as startAssignmentApi,
  swapTodayAssignment,
} from "@/features/experiments/api/experiments-api";
import { MOCK_EXPERIMENTS } from "@/features/experiments/data/mock-experiments";

interface ExperimentStore {
  experiments: Experiment[];
  assignments: ExperimentAssignment[];
  outcomes: ExperimentOutcome[];
  activeAssignmentId: string | null;
  loading: boolean;
  hydrated: boolean;
  hydrate: () => Promise<void>;
  getTodayAssignment: () => ExperimentAssignment | null;
  getExperimentForAssignment: (assignmentId: string) => Experiment | null;
  startAssignment: (assignmentId: string) => Promise<void>;
  completeAssignment: (
    assignmentId: string,
    outcome: Omit<ExperimentOutcome, "assignmentId">
  ) => Promise<void>;
  skipAssignment: (assignmentId: string) => Promise<void>;
  swapToday: () => Promise<void>;
  assignFromCatalog: (experimentId: string) => Promise<ExperimentAssignment | null>;
  setActive: (id: string | null) => void;
}

export const useExperimentStore = create<ExperimentStore>((set, get) => ({
  experiments: MOCK_EXPERIMENTS,
  assignments: [],
  outcomes: [],
  activeAssignmentId: null,
  loading: false,
  hydrated: false,

  hydrate: async () => {
    if (get().loading) return;
    set({ loading: true });
    try {
      let experiments = await listPublishedExperiments();
      const { assignment, experiment: todayExp } =
        await ensureTodayAssignment();
      if (
        todayExp &&
        !experiments.some((e) => e.id === todayExp.id)
      ) {
        experiments = [...experiments, todayExp];
      }
      const remoteOutcomes = await listOutcomes();
      const outcomes: ExperimentOutcome[] = remoteOutcomes.map((o) => ({
        assignmentId: o.assignmentId,
        completed: o.completed,
        afterMood: o.afterMood,
        afterEnergy: o.afterEnergy,
        repeatIntent: o.repeatIntent,
        ...(o.reflection ? { reflection: o.reflection } : {}),
      }));

      const assignments: ExperimentAssignment[] = assignment
        ? [assignment]
        : [];
      for (const o of remoteOutcomes) {
        if (!o.experimentId) continue;
        if (assignments.some((a) => a.id === o.assignmentId)) continue;
        assignments.push({
          id: o.assignmentId,
          experimentId: o.experimentId,
          status: "completed",
          recommendationReason: "",
          scheduledFor: null,
          startedAt: null,
          completedAt: o.completedAt,
        });
      }

      set({
        experiments,
        assignments,
        outcomes,
        loading: false,
        hydrated: true,
      });
    } catch {
      // Keep mock catalog usable even if network fails
      set({ loading: false, hydrated: true });
    }
  },

  getTodayAssignment: () => {
    const open = get().assignments.find(
      (a) => a.status === "assigned" || a.status === "started"
    );
    if (open) return open;
    return get().assignments.find((a) => a.status === "completed") ?? null;
  },

  getExperimentForAssignment: (assignmentId) => {
    const assignment = get().assignments.find((a) => a.id === assignmentId);
    if (!assignment) return null;
    const base =
      get().experiments.find((e) => e.id === assignment.experimentId) ?? null;
    if (!base) return null;
    const o = assignment.contentOverride;
    if (!o) return base;
    return {
      ...base,
      ...(o.title != null ? { title: o.title } : {}),
      ...(o.summary != null ? { summary: o.summary } : {}),
      ...(o.rationale != null ? { rationale: o.rationale } : {}),
      ...(o.instructions != null ? { instructions: o.instructions } : {}),
      ...(o.smallerVersion != null
        ? { smallerVersion: o.smallerVersion }
        : {}),
      ...(o.reflectionQuestions != null
        ? { reflectionQuestions: o.reflectionQuestions }
        : {}),
      ...(o.durationMinutes != null
        ? { durationMinutes: o.durationMinutes }
        : {}),
      ...(o.difficulty != null ? { difficulty: o.difficulty } : {}),
      ...(o.category != null ? { category: o.category } : {}),
    };
  },

  startAssignment: async (assignmentId) => {
    set((state) => ({
      assignments: state.assignments.map((a) =>
        a.id === assignmentId
          ? {
              ...a,
              status: "started" as AssignmentStatus,
              startedAt: new Date().toISOString(),
            }
          : a
      ),
      activeAssignmentId: assignmentId,
    }));
    await startAssignmentApi(assignmentId);
  },

  completeAssignment: async (assignmentId, outcome) => {
    set((state) => ({
      assignments: state.assignments.map((a) =>
        a.id === assignmentId
          ? {
              ...a,
              status: "completed" as AssignmentStatus,
              completedAt: new Date().toISOString(),
            }
          : a
      ),
      outcomes: [...state.outcomes, { assignmentId, ...outcome }],
      activeAssignmentId: null,
    }));
    await completeAssignmentApi(assignmentId, outcome);
  },

  skipAssignment: async (assignmentId) => {
    set((state) => ({
      assignments: state.assignments.map((a) =>
        a.id === assignmentId
          ? { ...a, status: "skipped" as AssignmentStatus }
          : a
      ),
      activeAssignmentId: null,
    }));
    await skipAssignmentApi(assignmentId);
  },

  swapToday: async () => {
    const current = get().getTodayAssignment();
    const { assignment, experiment } = await swapTodayAssignment(
      current && (current.status === "assigned" || current.status === "started")
        ? current.id
        : null
    );
    if (!assignment || !experiment) return;
    set((state) => ({
      experiments: state.experiments.some((e) => e.id === experiment.id)
        ? state.experiments
        : [...state.experiments, experiment],
      assignments: [
        assignment,
        ...state.assignments.filter(
          (a) =>
            a.id !== assignment.id &&
            a.status !== "assigned" &&
            a.status !== "started"
        ),
      ],
    }));
  },

  assignFromCatalog: async (experimentId) => {
    const { assignment, experiment } = await assignExperimentApi(experimentId);
    if (!assignment || !experiment) return null;
    set((state) => ({
      experiments: state.experiments.some((e) => e.id === experiment.id)
        ? state.experiments
        : [...state.experiments, experiment],
      assignments: [
        assignment,
        ...state.assignments.filter(
          (a) =>
            a.id !== assignment.id &&
            a.status !== "assigned" &&
            a.status !== "started"
        ),
      ],
    }));
    return assignment;
  },

  setActive: (id) => set({ activeAssignmentId: id }),
}));
